import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { Eyebrow, SectionNum } from "@/components/ui-bits";
import { LEGAL_LINK_GROUPS } from "@/data/legalLinks";
import { ExternalLink, ArrowUpRight } from "lucide-react";

function slugify(s) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function LegalLinks() {
  const totalLinks = LEGAL_LINK_GROUPS.reduce((n, g) => n + g.links.length, 0);

  return (
    <Layout>
      <SEO
        title="Legal Links"
        description="External references curated by Shelton Law PLLC — FCC and FAA regulations, export controls (ITAR, EAR, OFAC), FOCI, CFIUS, government contracting, CUI, intellectual property, artificial intelligence, CHIPS Act, and international treaties."
        path="/legal-links"
      />

      {/* Hero */}
      <section className="border-b border-hairline" data-testid="legal-links-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-8">
              <Eyebrow>Reference</Eyebrow>
              <h1 className="display-1 text-ink mt-6 max-w-4xl">Legal links.</h1>
              <p className="mt-8 text-ink-soft text-[1.0625rem] leading-relaxed max-w-2xl">
                A curated index of the federal regulations, treaties, and reference material the firm consults most often — organized by subject. External resources; not a substitute for legal advice.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <p className="section-num">
                {String(LEGAL_LINK_GROUPS.length).padStart(2, "0")} · Categories
              </p>
              <p className="text-[11px] uppercase tracking-[0.22em] text-ink-soft mt-2">
                {totalLinks} references
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured internal tools */}
      <section className="border-b border-hairline bg-ink text-ivory" data-testid="legal-links-featured">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <p className="text-[10px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-6">Firm tools</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="border border-ivory/15 p-6 flex flex-col">
              <h2 className="font-serif text-2xl leading-tight">CCL Navigator</h2>
              <p className="mt-2 text-ivory/70 text-[14px] leading-relaxed flex-grow">
                Search the Commerce Control List (15 CFR Part 774 Supp. 1) by ECCN or keyword. Every entry mirrored from eCFR, refreshed hourly.
              </p>
              <Link
                to="/ccl"
                className="mt-5 inline-flex items-center gap-2 bg-ivory text-ink px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass hover:text-ivory transition-colors self-start"
                data-testid="legal-links-ccl-cta"
              >
                Open CCL Navigator
                <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>
            <div className="border border-ivory/15 p-6 flex flex-col">
              <h2 className="font-serif text-2xl leading-tight">USML Navigator</h2>
              <p className="mt-2 text-ivory/70 text-[14px] leading-relaxed flex-grow">
                Browse the United States Munitions List (22 CFR § 121.1) by category and paragraph. All 21 categories mirrored from eCFR.
              </p>
              <Link
                to="/usml"
                className="mt-5 inline-flex items-center gap-2 bg-ivory text-ink px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-brass hover:text-ivory transition-colors self-start"
                data-testid="legal-links-usml-cta"
              >
                Open USML Navigator
                <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Table of contents */}
      <section className="border-b border-hairline bg-ivory-alt" data-testid="legal-links-toc">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
          <p className="eyebrow mb-4">On this page</p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            {LEGAL_LINK_GROUPS.map((g) => (
              <li key={g.heading}>
                <a
                  href={`#${slugify(g.heading)}`}
                  className="text-[13px] text-ink border-b border-brass/50 pb-0.5 hover:text-brass hover:border-brass transition-colors"
                  data-testid={`legal-links-toc-${slugify(g.heading)}`}
                >
                  {g.heading}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Groups */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16" data-testid="legal-links-content">
        {LEGAL_LINK_GROUPS.map((group, idx) => (
          <div
            key={group.heading}
            id={slugify(group.heading)}
            className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 py-12 border-t border-hairline first:border-t-0 scroll-mt-32"
            data-testid={`legal-links-section-${slugify(group.heading)}`}
          >
            <div className="lg:col-span-4">
              <SectionNum>{String(idx + 1).padStart(2, "0")}</SectionNum>
              <h2 className="font-serif text-3xl text-ink mt-3 leading-tight">{group.heading}</h2>
              <p className="mt-4 text-ink-soft text-[0.95rem] leading-snug">
                {group.links.length} reference{group.links.length === 1 ? "" : "s"}
              </p>
            </div>
            <div className="lg:col-span-8">
              <ul className="border-t border-hairline">
                {group.links.map((l) => (
                  <li key={l.href + l.label} className="border-b border-hairline-soft">
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-start justify-between gap-4 py-3 hover:bg-ivory-alt -mx-3 px-3 transition-colors"
                      data-testid={`legal-links-item-${slugify(group.heading)}-${slugify(l.label)}`}
                    >
                      <span className="text-[15px] text-ink leading-snug group-hover:text-brass transition-colors">
                        {l.label}
                      </span>
                      <ExternalLink className="w-4 h-4 mt-1 shrink-0 text-ink-soft group-hover:text-brass transition-colors" strokeWidth={2} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </section>

      {/* Disclaimer / CTA */}
      <section className="bg-ink text-ivory" data-testid="legal-links-outro">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 text-center">
          <p className="text-[11px] uppercase tracking-[0.28em] text-brass-light font-semibold mb-4">Note</p>
          <p className="font-serif text-2xl sm:text-3xl leading-snug tracking-tight">
            These references reflect published rules and materials as of the date the reader consults them. Nothing on this page is legal advice.
          </p>
        </div>
      </section>
    </Layout>
  );
}
